<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" aria-label="開啟帳戶選單">
          <span class="profile-dropdown__anchor">
            <slot />
            <VaAvatar :size="32" color="transparent">
              <img :src="authStore.user?.avatar || '/logo.png'" alt="使用者頭像" class="w-full h-full object-cover" />
            </VaAvatar>
            <span class="profile-dropdown__name">{{ authStore.user?.fullname || '帳戶' }}</span>
            <VaIcon name="va-arrow-down" size="14px" color="secondary" />
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <div class="profile-dropdown__summary">
          <div class="profile-dropdown__summary-name">{{ authStore.user?.fullname || '飛網世界' }}</div>
          <div class="profile-dropdown__summary-email admin-mono">{{ authStore.user?.email || '' }}</div>
          <VaBadge :text="roleLabel" :color="roleColor" />
        </div>
        <VaListSeparator class="mx-3 my-2" />
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useAuthStore } from '../../../../stores/auth-store'
import { useRouter } from 'vue-router'
import { USER_ROLE_LABELS, type UserRole } from '../../../../pages/users/types'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

const router = useRouter()
const authStore = useAuthStore()

const roleLabel = computed(() => USER_ROLE_LABELS[(authStore.user?.role || 'user') as UserRole] || '一般使用者')

const roleColor = computed(() => {
  if (authStore.user?.permissions?.isSuperAdmin) return 'danger'
  if (authStore.user?.permissions?.isAdmin) return 'warning'
  if (authStore.user?.permissions?.canWrite) return 'primary'
  return 'secondary'
})

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
          },
        ],
      },
      {
        name: 'explore',
        separator: true,
        list: [
          {
            name: 'shopline',
            href: 'https://www.flynetworld.com/',
            icon: 'mso-home',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'login',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const handleLogout = async () => {
  await authStore.logout()
  isShown.value = false
  router.push({ name: 'login' })
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    return { onClick: handleLogout }
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: max-content;
  }

  &__name {
    display: none;
    max-width: 140px;
    overflow: hidden;
    color: var(--va-text-primary);
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (min-width: 768px) {
      display: inline-block;
    }
  }

  &__summary {
    display: grid;
    gap: 0.25rem;
    padding: 0 1rem 0.5rem;
  }

  &__summary-name {
    color: var(--va-text-primary);
    font-weight: 800;
    line-height: 1.35;
  }

  &__summary-email {
    overflow-wrap: anywhere;
    color: var(--va-text-secondary);
    font-size: 0.75rem;
  }
}
</style>
